import { includes, map, uniq } from "lodash";

/**
 * What a variable is, in text: `{{name}}`, and the two questions the rest of the screen asks about one.
 *
 * 🔴 DOUBLE BRACES, and `name` never carries them. Confirmed with the reporter; AICC2-60 and AICC2-328 each
 * show the other form in places, and picking the wrong one silently turns every variable in every prompt into
 * plain text.
 *
 * The editor's side of this — a token becoming a chip and back again — moved to `promptMarkdown.ts` when
 * markdown became the stored format. What is left here is text-only and shared: the validator reads it, and so
 * does the dictionary.
 */

/** The class a chip is styled by, wherever it is rendered. */
export const PROMPT_VARIABLE_CLASS = "aicc-prompt-variable";

/**
 * 🔴 LETTERS, DIGITS AND UNDERSCORES. NOT DOTS. `{{ user_level }}` with spaces around the name is what a human
 * types and is fine; `{{a.b}}` is not a variable at all — the prompt engine throws on it every turn — and the
 * dot used to be accepted here, which turned a hard 400 into a chip that merely looked unfamiliar.
 *
 * Anything else brace-shaped is left alone rather than guessed at, and reported by `malformedVariablesIn`
 * below: an unclosed `{{` is a typo, and treating it as a variable would hide it.
 */
const VARIABLE_PATTERN_SOURCE = "\\{\\{\\s*(\\w+)\\s*\\}\\}";

/**
 * Everything `{{`-shaped in the text that the prompt engine will NOT read as a variable (AICC2-526's table).
 *
 * 🔴 THE BACKEND REJECTS THESE WITH A 400, AND THE EDITOR USED TO LET THEM THROUGH. Two shapes reach it:
 *
 * - a brace count that does not close — `{{language }` — which the engine silently drops the value for;
 * - a name with syntax in it — `{{a.b}}`, `{{x|upper}}` — which makes it throw on every turn.
 *
 * Neither becomes a chip, so nothing on the screen used to say anything about them: they read as ordinary
 * prose and the refusal arrived from the server, after the save.
 *
 * Reported as the exact text found rather than as a name, since what is wrong with them is their spelling.
 *
 * 🔴 THE BODY CARRIES NO BRACES AND THE MATCH ENDS AT THE FIRST ONE. `}}` closes a token, a single `}` ends a
 * broken one, and another `{{` or the line's end ends an unclosed one — so `{{language } about {{a.b}}` is two
 * findings rather than one long one, and neither swallows the prose between them. An earlier pattern stopped
 * at `{` without offering it as a terminator, which made a broken token followed by a good one invisible.
 */
const VARIABLE_CANDIDATE_SOURCE = "\\{\\{[^\\n{}]*(?:\\}\\}|\\}|(?=\\{\\{)|$)";

const WELL_FORMED = new RegExp(`^${VARIABLE_PATTERN_SOURCE}$`);

export function malformedVariablesIn(text: string): string[] {
  const pattern = new RegExp(VARIABLE_CANDIDATE_SOURCE, "gm");
  const found: string[] = [];
  let match = pattern.exec(text ?? "");

  while (match) {
    if (!WELL_FORMED.test(match[0])) {
      found.push(match[0]);
    }
    match = pattern.exec(text ?? "");
  }

  return uniq(found);
}

/** A fresh regex per call: a shared one carries `lastIndex` between callers and skips matches. */
const variablePattern = () => new RegExp(VARIABLE_PATTERN_SOURCE, "g");

export const promptVariableToken = (name: string) => `{{${name}}}`;

/**
 * What the dictionary says about the variables a chip may be. Absent `knownNames` means "do not judge" — a
 * caller that only knows which variables are required keeps working.
 */
export interface PromptVariableRules {
  requiredNames?: string[];
  knownNames?: string[];
}

/** Every variable the text uses, in the order it first uses them. */
export function variableNamesInText(text: string): string[] {
  const pattern = variablePattern();
  const names: string[] = [];
  let match = pattern.exec(text ?? "");

  while (match) {
    names.push(match[1]);
    match = pattern.exec(text ?? "");
  }

  return uniq(names);
}

/** Which of the required variables the text is missing — the check that disables Save (AC-4). */
export function missingRequiredVariables(text: string, requiredNames: string[]): string[] {
  const used = variableNamesInText(text);

  return map(
    requiredNames.filter((name) => !includes(used, name)),
    (name) => name
  );
}
